'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useStore } from '@nanostores/react';
import { toast } from 'sonner';
import {
  Check,
  Download,
  FileText,
  File as FileIcon,
  Image as ImageIcon,
  MoreHorizontal,
  Paperclip,
  X,
} from 'lucide-react';

import type { JsonValue } from '@lotushealth/lotushealth_js_sdk/schemas';

import {
  deletePriorAuthAttachment,
  downloadPriorAuthAttachment,
  usePriorAuthActionMutation,
  usePriorAuthDetailsQuery,
  type PriorAuthActionBody,
  type PriorAuthorizationAnswerDetailT,
  type PriorAuthorizationAnsweredQuestionT,
  type PriorAuthorizationAnswerRequestT,
  type PriorAuthorizationChoiceAnswerT,
  type PriorAuthorizationQuestionT,
  type PriorAuthorizationStatusTypeT,
} from '@/entities/prior-auth';
import { usePrescriptionQuery } from '@/entities/rx';
import { RxDraftSummaryNode } from '@/features/rx-form';
import { useProposedActionMutations } from '@/features/proposed-actions-plan/model/useProposedActionMutations';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { Checkbox } from '@/shared/ui/shadcn/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { HoldToSubmit } from '@/shared/ui/HoldToSubmit';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import { Switch } from '@/shared/ui/shadcn/switch';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/shadcn/tooltip';
import { closeCopilotOverlay } from '@/shared/stores/ui';
import { logger, type SourceProposedActionT } from '@/shared/lib';
import { cn } from '@/lib/utils';
import { useCopilotToolUiOptional } from '@/features/copilot/ui/ToolUi/useCopilotToolUi';

import {
  $priorAuthActionPrefill,
  clearPriorAuthActionPrefill,
  type PriorAuthActionPrefill,
} from '../model/priorAuthActionPrefill';

/** Strip the `data:<mime>;base64,` prefix that ``FileReader.readAsDataURL`` emits. */
function stripDataUrlPrefix(dataUrl: string): string {
  const commaIdx = dataUrl.indexOf(',');
  return commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
}

async function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected file reader result'));
        return;
      }
      resolve(stripDataUrlPrefix(result));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Attachment filename → Lucide icon. Just enough fidelity to visually
 * distinguish PDFs from images from "anything else" — DoseSpot doesn't
 * give us a content-type so we go off the extension.
 */
function attachmentIcon(fileName: string | null | undefined) {
  const name = (fileName ?? '').toLowerCase();
  if (name.endsWith('.pdf')) return FileText;
  if (/\.(png|jpe?g|gif|webp|bmp|tiff?|svg|heic)$/i.test(name)) return ImageIcon;
  return FileIcon;
}

/** Pretty short upload date for the attachment row, e.g. "May 1, 2026". */
function formatAttachmentDate(stamp: string | null | undefined): string | null {
  if (!stamp) return null;
  const parsed = new Date(stamp);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Hand a downloaded blob to the browser as a "Save As" prompt. Pulled
 * out so the entity-layer ``downloadPriorAuthAttachment`` (which only
 * knows about transport) stays free of DOM concerns.
 */
function triggerBrowserDownload(blob: Blob, suggestedFileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = suggestedFileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Defer revocation a tick so the browser can start the save.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}

function toJsonValueRecord(value: Record<string, unknown>): Record<string, JsonValue> {
  const result: Record<string, JsonValue> = {};
  Object.entries(value).forEach(([key, entryValue]) => {
    if (entryValue !== null && entryValue !== undefined) {
      result[key] = entryValue as JsonValue;
    }
  });
  return result;
}

/** Render a single answer detail row as a plain string for the transcript. */
function formatAnswerDetail(detail: PriorAuthorizationAnswerDetailT): string {
  if (detail.Answer !== null && detail.Answer !== undefined && detail.Answer !== '') {
    return detail.Answer;
  }
  if (
    detail.AnswerFreeText !== null &&
    detail.AnswerFreeText !== undefined &&
    detail.AnswerFreeText !== ''
  ) {
    return detail.AnswerFreeText;
  }
  if (detail.AnswerNumeric !== null && detail.AnswerNumeric !== undefined) {
    return String(detail.AnswerNumeric);
  }
  if (detail.AnswerDateTime !== null && detail.AnswerDateTime !== undefined) {
    return String(detail.AnswerDateTime);
  }
  return '—';
}

/** Flatten all answered questions across every question set in the history. */
function collectAnsweredQuestions(
  questionSets: Array<
    { AnsweredQuestions?: PriorAuthorizationAnsweredQuestionT[] | null } | undefined
  >
): PriorAuthorizationAnsweredQuestionT[] {
  const flat: PriorAuthorizationAnsweredQuestionT[] = [];
  for (const set of questionSets) {
    for (const aq of set?.AnsweredQuestions ?? []) {
      flat.push(aq);
    }
  }
  return flat;
}

// DoseSpot `CurrentStatus` values that mean the case is closed — no more
// submit / cancel / attachment actions make sense. Appeal is still allowed
// from some of these via ``CanAppeal``.
const TERMINAL_STATUSES = new Set<PriorAuthorizationStatusTypeT>([
  'Approved',
  'Denied',
  'PartiallyDenied',
  'Canceled',
  'Closed',
  'Deleted',
  'NotRequired',
]);

// Payer-owned statuses where the clinician has no action to take — the case
// is in the payer's court and we're waiting for their next move.
const WAITING_STATUSES = new Set<PriorAuthorizationStatusTypeT>([
  'AppealRequested',
  'Pending',
  'Requested',
  'InProcess',
  'CancelRequested',
]);

/**
 * Wizard for acting on a Prior Authorization case.
 *
 * Conversation-style UI:
 * - Previously answered questions are shown as a greyed-out transcript at
 *   the top (payer's question + clinician's answer).
 * - Any currently pending question is rendered below as an editable form.
 * - The primary footer button ("Submit answer" while a question is pending,
 *   "Submit to payer" once the question set is exhausted) is disabled until
 *   the clinician has actually supplied a valid answer. Upload Attachment
 *   lives next to it as a secondary button so the clinician can drop in a
 *   PDF without leaving the flow.
 * - Appeal / Deny / Cancel are tucked into an overflow menu and only
 *   surfaced when the case state makes them meaningful (``CanAppeal``,
 *   offline cases, not-yet-closed cases).
 *
 * Driven by ``$priorAuthActionPrefill``. The prefill always carries a
 * ``recordId`` (our internal UUID) so we can fetch live case details from
 * ``usePriorAuthDetailsQuery`` — and a ``caseId`` so we can post actions
 * against DoseSpot without a second round-trip.
 */
export function PriorAuthActionWizard() {
  const incomingPrefill = useStore($priorAuthActionPrefill);
  const mutations = useProposedActionMutations();
  const priorAuthActionMutation = usePriorAuthActionMutation();
  const sourceProposedActionRef = useRef<SourceProposedActionT | null>(
    incomingPrefill?.sourceProposedAction ??
      (incomingPrefill?.action ? { externalId: incomingPrefill.action.external_id } : null)
  );

  // Latch the incoming prefill into local state. We immediately clear
  // ``$priorAuthActionPrefill`` below (so subsequent opens always start
  // from a fresh atom write) — if we read ``caseId`` / ``recordId`` off
  // the live atom we'd see ``null`` on every render *after* the clear,
  // dropping "Case #100005" to "Case #0" and disabling the details
  // fetch that the pending-question panel depends on.
  const [activePrefill, setActivePrefill] = useState<PriorAuthActionPrefill | null>(
    incomingPrefill
  );

  const [submitting, setSubmitting] = useState(false);

  const [selectedChoices, setSelectedChoices] = useState<number[]>([]);
  const [freeTextAnswer, setFreeTextAnswer] = useState('');
  const [numericAnswer, setNumericAnswer] = useState('');
  const [dateAnswer, setDateAnswer] = useState('');

  const [appealOpen, setAppealOpen] = useState(false);
  const [appealReason, setAppealReason] = useState('');

  // Ephemeral footer toggle: when on, after the final ``answer_question``
  // submit succeeds we ask the copilot to draft an LMN for this case so
  // the clinician can iterate on it in chat. Resets on wizard re-prefill.
  const [generateLmn, setGenerateLmn] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);

  const copilotToolUi = useCopilotToolUiOptional();

  const [formError, setFormError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const detailsQuery = usePriorAuthDetailsQuery(activePrefill?.recordId);

  useEffect(() => {
    if (!incomingPrefill) return;
    if (activePrefill === incomingPrefill) return;

    setActivePrefill(incomingPrefill);
    sourceProposedActionRef.current =
      incomingPrefill.sourceProposedAction ??
      (incomingPrefill.action ? { externalId: incomingPrefill.action.external_id } : null);

    setSelectedChoices([]);
    setFreeTextAnswer('');
    setNumericAnswer('');
    setDateAnswer('');
    setAppealOpen(false);
    setAppealReason('');
    setFormError(null);
    setGenerateLmn(false);

    clearPriorAuthActionPrefill();
  }, [activePrefill, incomingPrefill]);

  const caseId = activePrefill?.caseId ?? 0;
  const recordId = activePrefill?.recordId ?? '';
  const patientUserId = activePrefill?.patientUserId;

  const dosespotPrescriptionId = useMemo(() => {
    if (activePrefill?.dosespotPrescriptionId) return activePrefill.dosespotPrescriptionId;
    const fromCase = detailsQuery.data?.case.PrescriptionId;
    return fromCase != null ? String(fromCase) : '';
  }, [activePrefill?.dosespotPrescriptionId, detailsQuery.data]);

  const prescriptionQuery = usePrescriptionQuery(patientUserId, dosespotPrescriptionId);

  const pendingQuestion: PriorAuthorizationQuestionT | null = useMemo(() => {
    if (!detailsQuery.data) return null;
    return detailsQuery.data.pendingQuestion ?? null;
  }, [detailsQuery.data]);

  const pendingQuestionId = useMemo(() => {
    const raw = detailsQuery.data?.case.NextQuestionId;
    return typeof raw === 'number' ? raw : null;
  }, [detailsQuery.data]);

  const answeredQuestions = useMemo(() => {
    return collectAnsweredQuestions(detailsQuery.data?.history.QuestionSets ?? []);
  }, [detailsQuery.data]);

  const answerType = pendingQuestion?.AnswerType ?? null;
  const currentStatus = detailsQuery.data?.case.CurrentStatus ?? null;
  const isTerminal = currentStatus !== null && TERMINAL_STATUSES.has(currentStatus);
  const isWaiting = currentStatus !== null && WAITING_STATUSES.has(currentStatus);
  // ``CanAppeal`` lives on ``case.FinalStatus`` as a bool, but the history
  // endpoint echoes it back as a "Yes"/"No" string. Support both.
  const canAppealFromCase = detailsQuery.data?.case.FinalStatus?.CanAppeal ?? null;
  const canAppealFromHistory = detailsQuery.data?.history.CanAppeal ?? null;
  const canAppeal =
    canAppealFromCase === true ||
    (typeof canAppealFromHistory === 'string' && canAppealFromHistory.toLowerCase() === 'yes');
  const isOffline = detailsQuery.data?.case.IsOffline === true;
  const isExpedited = detailsQuery.data?.case.IsExpedited === true;

  const hasPendingQuestion = pendingQuestion !== null && pendingQuestionId !== null;

  /** Whether the answer form is filled out well enough to submit. */
  const answerIsComplete = useMemo(() => {
    if (!pendingQuestion) return false;
    switch (answerType) {
      case 'Select':
      case 'Multiselect':
        return selectedChoices.length > 0;
      case 'FreeText':
        return freeTextAnswer.trim().length > 0;
      case 'Numeric':
        return Number.isFinite(Number(numericAnswer)) && numericAnswer.trim().length > 0;
      case 'Date':
      case 'DateTime':
        return dateAnswer.length > 0;
      default:
        return false;
    }
  }, [answerType, dateAnswer, freeTextAnswer, numericAnswer, pendingQuestion, selectedChoices]);

  // What the primary footer button does right now — either answer the
  // open question or submit the completed question set back to the payer.
  const primaryMode: 'answer_question' | 'submit' = hasPendingQuestion
    ? 'answer_question'
    : 'submit';
  // Once the case has moved into a waiting status the answer/submit work
  // is done; we lock the button to a "Submitted to payer" pill so the
  // clinician sees the finalised state instead of an inert
  // "Submit to payer" that confusingly looks pressable.
  const primaryLabel = isWaiting
    ? 'Submitted to payer'
    : submitting
      ? 'Submitting…'
      : 'Hold to submit';
  const primaryDisabled =
    submitting ||
    isTerminal ||
    isWaiting ||
    (primaryMode === 'answer_question' && !answerIsComplete);

  const handleFilePick = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      if (!caseId) {
        setFormError('Missing case id for this prior auth.');
        return;
      }
      try {
        const b64 = await readFileAsBase64(file);
        setSubmitting(true);
        setFormError(null);
        await priorAuthActionMutation.mutateAsync({
          caseId,
          request: {
            action: 'upload_attachment',
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            contentBase64: b64,
          },
          recordId: recordId || undefined,
          patientUserId,
        });
        toast.success('Attachment uploaded.', { description: file.name });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setFormError(`Attachment upload failed: ${message}`);
        logger.error('priorAuth.uploadAttachment.failed', { err });
      } finally {
        setSubmitting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [caseId, patientUserId, priorAuthActionMutation, recordId]
  );

  const buildAnswerPayload = useCallback((): PriorAuthorizationAnswerRequestT | null => {
    if (!pendingQuestion) return null;
    const payload: PriorAuthorizationAnswerRequestT = {};

    switch (answerType) {
      case 'Select':
      case 'Multiselect': {
        const choices = (pendingQuestion.AnswerChoices ?? []).filter(
          (c): c is typeof c & { QuestionChoiceId: number } =>
            typeof c.QuestionChoiceId === 'number' && selectedChoices.includes(c.QuestionChoiceId)
        );
        if (choices.length === 0) return null;
        const selected: PriorAuthorizationChoiceAnswerT[] = choices.map((c) => ({
          PriorAuthorizationQuestionChoiceId: c.QuestionChoiceId,
          PriorAuthorizationQuestionChoiceText: c.QuestionChoiceText ?? null,
        }));
        payload.PriorAuthorizationQuestionChoices = selected;
        if (freeTextAnswer.trim().length > 0) {
          payload.FreeTextAnswer = freeTextAnswer.trim();
        }
        return payload;
      }
      case 'FreeText': {
        if (!freeTextAnswer.trim()) return null;
        payload.FreeTextAnswer = freeTextAnswer.trim();
        return payload;
      }
      case 'Numeric': {
        const parsed = Number(numericAnswer);
        if (!Number.isFinite(parsed)) return null;
        payload.NumericAnswer = parsed;
        return payload;
      }
      case 'Date':
      case 'DateTime': {
        if (!dateAnswer) return null;
        payload.DateTimeAnswer = dateAnswer;
        return payload;
      }
      default:
        return null;
    }
  }, [answerType, dateAnswer, freeTextAnswer, numericAnswer, pendingQuestion, selectedChoices]);

  const resetAnswerInputs = useCallback(() => {
    setSelectedChoices([]);
    setFreeTextAnswer('');
    setNumericAnswer('');
    setDateAnswer('');
  }, []);

  /** Run the primary action: either answer the current question or submit. */
  const handlePrimary = useCallback(async () => {
    if (!caseId) {
      setFormError('Missing case id for this prior auth.');
      return;
    }

    setFormError(null);
    setSubmitting(true);

    try {
      let req: PriorAuthActionBody;
      if (primaryMode === 'answer_question') {
        if (!pendingQuestionId) {
          setFormError('No pending question to answer.');
          return;
        }
        const answer = buildAnswerPayload();
        if (!answer) {
          setFormError('Please provide an answer before submitting.');
          return;
        }
        req = { action: 'answer_question', questionId: pendingQuestionId, answer };
      } else {
        req = { action: 'submit' };
      }

      const response = await priorAuthActionMutation.mutateAsync({
        caseId,
        request: req,
        recordId: recordId || undefined,
        patientUserId,
      });

      // Answer chains: DoseSpot sometimes issues the PA question set one at
      // a time, driven by branching logic on prior answers. When the server
      // echoes back a ``nextQuestionId`` we keep the wizard open — the
      // mutation already invalidates the details query above, so the new
      // ``pendingQuestion`` refetches on the next tick and the form
      // re-renders with fresh choices.
      const hasMoreQuestions =
        primaryMode === 'answer_question' &&
        response.nextQuestionId !== null &&
        response.nextQuestionId !== undefined;

      const source = sourceProposedActionRef.current;
      if (source && !hasMoreQuestions) {
        const toolOutput = toJsonValueRecord({
          caseId,
          action: primaryMode,
          nextQuestionId: response.nextQuestionId ?? null,
        });
        await mutations.finalizeFromSource(source, toolOutput).catch((err: unknown) => {
          logger.error('priorAuth.finalizeFromSource.failed', {
            externalId: source.externalId,
            err,
          });
        });
      }

      if (hasMoreQuestions) {
        toast.success('Answer recorded. Next question loaded.');
        resetAnswerInputs();
        setFormError(null);
      } else if (primaryMode === 'answer_question') {
        toast.success('Answer recorded. Ready to submit to payer.');
        resetAnswerInputs();
      } else {
        toast.success('Prior auth submitted to payer.');
        // Footer "Generate LMN" toggle: fires on submit-to-payer so the
        // toggle's enabled-state ("all questions answered") matches when
        // drafting actually happens, and the LLM has the full payer Q&A
        // as context. Sending the case number — not the internal record
        // id — so the LMN tools' resolver path runs (and we don't depend
        // on the LLM hallucinating UUIDs).
        if (generateLmn && caseId) {
          const sent = copilotToolUi?.onSendMessage;
          if (sent) {
            sent(`Draft a Letter of Medical Necessity for prior authorization case #${caseId}.`);
          } else {
            logger.warn('priorAuth.generateLmn.noCopilotContext', { caseId });
          }
        }
        // Don't auto-close: the clinician should see the wizard transition
        // to the locked "Submitted to payer" state so they have a clear
        // visual confirmation that the case is now with the payer.
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setFormError(`Action failed: ${message}`);
      logger.error('priorAuth.submit.failed', { mode: primaryMode, err });
    } finally {
      setSubmitting(false);
    }
  }, [
    buildAnswerPayload,
    caseId,
    copilotToolUi,
    generateLmn,
    mutations,
    patientUserId,
    pendingQuestionId,
    primaryMode,
    priorAuthActionMutation,
    recordId,
    resetAnswerInputs,
  ]);

  /** One-off terminal actions from the overflow menu (cancel / deny / appeal). */
  const runSimpleAction = useCallback(
    async (req: PriorAuthActionBody, successToast: string) => {
      if (!caseId) {
        setFormError('Missing case id for this prior auth.');
        return;
      }
      setFormError(null);
      setSubmitting(true);
      try {
        await priorAuthActionMutation.mutateAsync({
          caseId,
          request: req,
          recordId: recordId || undefined,
          patientUserId,
        });

        const source = sourceProposedActionRef.current;
        if (source) {
          const toolOutput = toJsonValueRecord({ caseId, action: req.action });
          await mutations.finalizeFromSource(source, toolOutput).catch((err: unknown) => {
            logger.error('priorAuth.finalizeFromSource.failed', {
              externalId: source.externalId,
              err,
            });
          });
        }

        toast.success(successToast);
        closeCopilotOverlay('priorAuthAction');
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setFormError(`Action failed: ${message}`);
        logger.error('priorAuth.simpleAction.failed', { action: req.action, err });
      } finally {
        setSubmitting(false);
      }
    },
    [caseId, mutations, patientUserId, priorAuthActionMutation, recordId]
  );

  const handleAppealConfirm = useCallback(async () => {
    if (!appealReason.trim()) {
      setFormError('Appeal reason is required.');
      return;
    }
    await runSimpleAction({ action: 'appeal', appealReason: appealReason.trim() }, 'Appeal filed.');
    setAppealOpen(false);
    setAppealReason('');
  }, [appealReason, runSimpleAction]);

  const toggleChoice = (choiceId: number) => {
    if (answerType === 'Select') {
      setSelectedChoices([choiceId]);
      return;
    }
    setSelectedChoices((prev) =>
      prev.includes(choiceId) ? prev.filter((id) => id !== choiceId) : [...prev, choiceId]
    );
  };

  // Which of the destructive/terminal actions are surfaced right now?
  const showCancel = !isTerminal;
  const showDeny = !isTerminal && isOffline;
  const showAppeal = canAppeal;
  const hasOverflowActions = showCancel || showDeny || showAppeal;

  if (!activePrefill) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        No prior authorization selected.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold">Prior Authorization — Case #{caseId}</div>
        {currentStatus ? (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {currentStatus}
          </span>
        ) : null}
        {isExpedited ? (
          <span className="rounded-full border border-orange-400/50 bg-orange-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-orange-700 dark:border-orange-500/30 dark:bg-orange-950/40 dark:text-orange-300">
            Expedited
          </span>
        ) : null}
      </div>

      {detailsQuery.isLoading ? (
        <div className="text-xs text-muted-foreground">Loading case details…</div>
      ) : null}

      {detailsQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load case</AlertTitle>
          <AlertDescription>
            {detailsQuery.error instanceof Error
              ? detailsQuery.error.message
              : 'Unknown error loading case.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {formError ? (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">{formError}</AlertDescription>
        </Alert>
      ) : null}

      {prescriptionQuery.data ? (
        <section
          aria-label="Prescription"
          className="flex-none rounded-md border border-border bg-muted/30 p-2.5"
        >
          <RxDraftSummaryNode
            className="text-xs [&>*]:space-y-1"
            medicationDisplayName={prescriptionQuery.data.DisplayName ?? '—'}
            directions={prescriptionQuery.data.Directions ?? '—'}
            quantity={String(prescriptionQuery.data.Quantity ?? '')}
            refills={String(prescriptionQuery.data.Refills ?? '')}
            daysSupply={String(prescriptionQuery.data.DaysSupply ?? '')}
            pharmacyNotes={
              prescriptionQuery.data.PharmacyNotes
                ? String(prescriptionQuery.data.PharmacyNotes)
                : undefined
            }
            noSubstitutions={prescriptionQuery.data.NoSubstitutions === true}
          />
        </section>
      ) : prescriptionQuery.isLoading ? (
        <div className="text-xs text-muted-foreground">Loading prescription…</div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
        {answeredQuestions.length > 0 ? (
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Answered questions
            </Label>
            <ol className="space-y-2">
              {answeredQuestions.map((aq, idx) => (
                <li
                  key={`${idx}-${aq.Question ?? 'q'}`}
                  className="rounded-md border border-border bg-muted/30 p-2 opacity-70"
                >
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                      <Check className="h-3 w-3" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="text-xs font-medium leading-relaxed whitespace-pre-wrap">
                        {aq.Question ?? '—'}
                      </p>
                      {(aq.Answers ?? []).length > 0 ? (
                        <ul className="space-y-0.5">
                          {(aq.Answers ?? []).map((ans, aIdx) => (
                            <li
                              key={aIdx}
                              className="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap"
                            >
                              {formatAnswerDetail(ans)}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">No answer recorded.</p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ) : null}

        {isWaiting ? (
          <Alert>
            <AlertDescription className="text-xs">
              This case is waiting for payer review ({currentStatus}).
            </AlertDescription>
          </Alert>
        ) : null}

        {hasPendingQuestion && pendingQuestion ? (
          <div className="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-2.5">
            <Label className="text-[11px] font-medium uppercase tracking-wide text-primary">
              Pending question
            </Label>
            <p className="text-xs font-medium leading-relaxed whitespace-pre-wrap">
              {pendingQuestion.QuestionText ?? '—'}
            </p>

            {(answerType === 'Select' || answerType === 'Multiselect') &&
            pendingQuestion.AnswerChoices ? (
              <div className="space-y-2">
                <Label className="text-xs">
                  {answerType === 'Select' ? 'Choose one' : 'Choose one or more'}
                </Label>
                <div className="flex flex-col gap-2">
                  {pendingQuestion.AnswerChoices.map((choice) => {
                    const id = choice.QuestionChoiceId ?? -1;
                    const checked = selectedChoices.includes(id);
                    return (
                      <label key={id} className="flex items-start gap-2 text-xs leading-relaxed">
                        <Checkbox checked={checked} onCheckedChange={() => toggleChoice(id)} />
                        <span>{choice.QuestionChoiceText ?? '—'}</span>
                      </label>
                    );
                  })}
                </div>
                <div>
                  <Label className="text-xs">Additional notes (optional)</Label>
                  <Textarea
                    value={freeTextAnswer}
                    onChange={(e) => setFreeTextAnswer(e.target.value)}
                    rows={2}
                    className="text-xs"
                  />
                </div>
              </div>
            ) : null}

            {answerType === 'FreeText' ? (
              <div>
                <Label className="text-xs">Answer</Label>
                <Textarea
                  value={freeTextAnswer}
                  onChange={(e) => setFreeTextAnswer(e.target.value)}
                  rows={3}
                  className="text-xs"
                />
              </div>
            ) : null}

            {answerType === 'Numeric' ? (
              <div>
                <Label className="text-xs">Answer (numeric)</Label>
                <Input
                  type="number"
                  value={numericAnswer}
                  onChange={(e) => setNumericAnswer(e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
            ) : null}

            {answerType === 'Date' || answerType === 'DateTime' ? (
              <div>
                <Label className="text-xs">
                  {answerType === 'Date' ? 'Answer (date)' : 'Answer (date & time)'}
                </Label>
                <Input
                  type={answerType === 'Date' ? 'date' : 'datetime-local'}
                  value={dateAnswer}
                  onChange={(e) => setDateAnswer(e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
            ) : null}

            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Submitting will commit your answer to DoseSpot — answers can&apos;t be revised once
              submitted.
            </p>
          </div>
        ) : !detailsQuery.isLoading && !isTerminal && !isWaiting ? (
          <div className="space-y-1.5 rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
            <p>All questions are answered. Hold the button below to send the case to the payer.</p>
            <p className="text-[11px] leading-relaxed">
              Submitting hands the case off to the payer for adjudication. You can still attach
              additional documents afterward (e.g. an LMN) but answers can&apos;t be revised.
            </p>
          </div>
        ) : null}

        {isTerminal ? (
          <Alert>
            <AlertDescription className="text-xs">
              {canAppeal
                ? `This case was ${currentStatus?.toLowerCase()}. You can file an appeal using the menu below.`
                : `This case is closed (${currentStatus}). No further answers can be submitted.`}
            </AlertDescription>
          </Alert>
        ) : null}

        {appealOpen ? (
          <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2.5">
            <Label className="text-xs font-medium">Appeal reason</Label>
            <Textarea
              value={appealReason}
              onChange={(e) => setAppealReason(e.target.value)}
              rows={3}
              className="text-xs"
              placeholder="Explain why this case should be reconsidered…"
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setAppealOpen(false);
                  setAppealReason('');
                  setFormError(null);
                }}
                className="text-xs"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleAppealConfirm}
                className="text-xs"
                disabled={submitting || appealReason.trim().length === 0}
              >
                File appeal
              </Button>
            </div>
          </div>
        ) : null}

        {(detailsQuery.data?.case.Attachments ?? []).length > 0 ? (
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Attachments
            </Label>
            <ul className="space-y-2">
              {(detailsQuery.data?.case.Attachments ?? []).map((att, idx) => {
                const fileName = att.FileName ?? `attachment-${att.AttachmentId ?? idx}`;
                const Icon = attachmentIcon(fileName);
                const uploaded = formatAttachmentDate(att.CreatedDateStamp ?? null);
                const attachmentIdRaw = att.AttachmentId;
                const attachmentId =
                  typeof attachmentIdRaw === 'number'
                    ? attachmentIdRaw
                    : typeof attachmentIdRaw === 'string'
                      ? Number(attachmentIdRaw)
                      : null;
                const canDownload =
                  caseId > 0 && attachmentId !== null && !Number.isNaN(attachmentId);
                return (
                  <li
                    key={`${att.AttachmentId ?? idx}-${fileName}`}
                    className="rounded-md border border-border bg-muted/30 p-2"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium leading-tight">{fileName}</p>
                        {uploaded ? (
                          <p className="text-[11px] leading-tight text-muted-foreground">
                            Uploaded {uploaded}
                          </p>
                        ) : null}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        disabled={!canDownload}
                        onClick={() => {
                          if (!canDownload || attachmentId === null) return;
                          downloadPriorAuthAttachment(caseId, attachmentId)
                            .then((blob) => {
                              triggerBrowserDownload(
                                blob,
                                fileName || `pa-${caseId}-attachment-${attachmentId}`
                              );
                            })
                            .catch((err: unknown) => {
                              const msg = err instanceof Error ? err.message : 'Unknown error';
                              toast.error('Could not download attachment', {
                                description: msg,
                              });
                              logger.error('priorAuth.attachment.download.failed', {
                                caseId,
                                attachmentId,
                                err,
                              });
                            });
                        }}
                        title="Download"
                        aria-label={`Download ${fileName}`}
                      >
                        <Download className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                        disabled={
                          !canDownload ||
                          submitting ||
                          isTerminal ||
                          deletingAttachmentId === attachmentId
                        }
                        onClick={() => {
                          if (!canDownload || attachmentId === null) return;
                          setDeletingAttachmentId(attachmentId);
                          deletePriorAuthAttachment(caseId, attachmentId)
                            .then(async () => {
                              toast.success('Attachment removed.', { description: fileName });
                              await detailsQuery.refetch();
                            })
                            .catch((err: unknown) => {
                              const msg = err instanceof Error ? err.message : 'Unknown error';
                              // DoseSpot sometimes refuses post-submit deletes;
                              // we don't pretend the row is gone in that case.
                              toast.error('Could not remove attachment', { description: msg });
                              logger.error('priorAuth.attachment.delete.failed', {
                                caseId,
                                attachmentId,
                                err,
                              });
                            })
                            .finally(() => {
                              setDeletingAttachmentId((current) =>
                                current === attachmentId ? null : current
                              );
                            });
                        }}
                        title="Remove"
                        aria-label={`Remove ${fileName}`}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="flex flex-none items-center justify-between gap-2 border-t border-border pt-2">
        <div className="flex items-center gap-1">
          <input
            ref={fileInputRef}
            type="file"
            onChange={(e) => void handleFilePick(e)}
            className="sr-only"
            aria-label="Upload attachment"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="text-xs"
            // ``isWaiting`` is intentionally NOT in here: the LMN
            // sign-and-attach flow uploads after the case has moved into
            // a payer-owned status (``AttachmentRequired=true`` only ever
            // comes back post-submit), so we keep Attach live until the
            // case is fully terminal (Approved / Denied / Cancelled / …).
            disabled={submitting || isTerminal}
            title="Upload attachment"
          >
            <Paperclip className="mr-1 h-3.5 w-3.5" aria-hidden />
            Attach
          </Button>
          {(() => {
            // Greyed out until the question chain is exhausted: the LMN
            // draft only fires on submit-to-payer, and the LLM needs the
            // full Q&A as context, so flipping it earlier would just be
            // misleading.
            const lmnDisabled = submitting || isTerminal || isWaiting || hasPendingQuestion;
            const toggleInner = (
              <>
                <Switch
                  checked={generateLmn && !lmnDisabled}
                  onCheckedChange={setGenerateLmn}
                  disabled={lmnDisabled}
                  aria-label="Generate Letter of Medical Necessity on submit to payer"
                />
                <span>Generate LMN</span>
              </>
            );
            const labelClass = cn(
              'ml-2 inline-flex items-center gap-2 text-xs select-none',
              lmnDisabled ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'
            );
            // Only wrap in a tooltip while disabled-due-to-pending-questions —
            // we don't want a "submitting…" tooltip flashing in normal use.
            if (hasPendingQuestion && !isTerminal && !isWaiting) {
              return (
                <Tooltip>
                  {/* asChild forwards Radix's ref/handlers onto the <label>
                      directly so the tooltip anchors to the toggle's bounding
                      box (without an extra wrapper that would offset it). */}
                  <TooltipTrigger asChild>
                    <label className={labelClass}>{toggleInner}</label>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="start" sideOffset={6}>
                    Answer all payer questions to enable LMN drafting.
                  </TooltipContent>
                </Tooltip>
              );
            }
            return <label className={labelClass}>{toggleInner}</label>;
          })()}
          {hasOverflowActions ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs"
                  disabled={submitting}
                  title="More actions"
                  aria-label="More actions"
                >
                  <MoreHorizontal className="h-4 w-4" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top">
                {showAppeal ? (
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      setAppealOpen(true);
                      setFormError(null);
                    }}
                  >
                    Appeal…
                  </DropdownMenuItem>
                ) : null}
                {showDeny ? (
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      void runSimpleAction({ action: 'deny' }, 'Case marked denied.');
                    }}
                  >
                    Deny
                  </DropdownMenuItem>
                ) : null}
                {showCancel ? (
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={(e) => {
                      e.preventDefault();
                      void runSimpleAction({ action: 'cancel' }, 'Case cancelled.');
                    }}
                  >
                    Cancel case
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => closeCopilotOverlay('priorAuthAction')}
            className="text-xs"
            disabled={submitting}
          >
            Close
          </Button>
          <HoldToSubmit
            onSubmit={() => {
              void handlePrimary();
            }}
            disabled={primaryDisabled}
            label={primaryLabel}
          />
        </div>
      </div>
    </div>
  );
}
